import React, { useState, useRef, useEffect } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { Message } from '../types';

const ChatApp: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello there!\n\nHow can I help you today?',
      sender: 'assistant',
      timestamp: Date.now()
    }
  ]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Check auth status on mount
  useEffect(() => {
    fetch('/auth/status')
      .then(res => {
        if (res.status === 401) {
          setAuthError(true);
        }
      })
      .catch(() => {
        // Network error — don't block the UI
      });
  }, []);

  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const threshold = 100; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  const scrollToBottom = () => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle scroll events to detect if user scrolled up
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const nearBottom = isNearBottom();
    
    // If user scrolls during streaming, stop auto-scrolling
    if (isStreaming && !nearBottom) {
      setUserScrolledUp(true);
    }
    
    // Show/hide scroll button based on position
    setShowScrollButton(!nearBottom);
  };

  const scrollToBottomClick = () => {
    setUserScrolledUp(false);
    setShowScrollButton(false);
    scrollToBottom();
  };

  const handleSuggestedPrompt = (prompt: string) => {
    streamResponse(prompt);
  };

  // Check if we're on the welcome screen (only welcome message)
  const isWelcomeScreen = messages.length === 1 && messages[0].sender === 'assistant';

  useEffect(() => {
    // Auto-scroll in these cases:
    // 1. User sends a message (always scroll)
    // 2. During streaming AND user hasn't scrolled up
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage?.sender === 'user') {
      // Always scroll when user sends a message
      setUserScrolledUp(false);
      scrollToBottom();
    } else if (isStreaming && !userScrolledUp) {
      // Only auto-scroll during streaming if user hasn't scrolled up
      scrollToBottom();
    }
  }, [messages, isStreaming, userScrolledUp]);

  const addMessage = (text: string, sender: 'user' | 'assistant') => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  const updateLastMessage = (text: string) => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        text
      };
      return updated;
    });
  };

  const streamResponse = async (message: string) => {
    setIsStreaming(true);
    setInputDisabled(true);
    setUserScrolledUp(false); // Reset scroll state when new message sent
    
    // Add user message
    addMessage(message, 'user');
    
    // Add assistant message placeholder with empty text to show loading indicator
    addMessage('', 'assistant');
    let accumulatedText = '';

    try {
      const response = await fetch('/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message })
      });

      if (response.status === 401) {
        setAuthError(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.text) {
                  accumulatedText += data.text;
                  updateLastMessage(accumulatedText);
                  
                  // Add small delay to make streaming more visible
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
                if (data.done) {
                  return;
                }
                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Error:', error);
      updateLastMessage('Sorry, there was an error processing your message.');
    } finally {
      setIsStreaming(false);
      setInputDisabled(false);
      // Reset scroll state and scroll to bottom when streaming ends
      setUserScrolledUp(false);
      scrollToBottom();
    }
  };

  const handleNewSession = async () => {
    try {
      const response = await fetch('/new-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 401) {
        setAuthError(true);
        return;
      }

      if (response.ok) {
        // Clear chat history and reset scroll state
        setUserScrolledUp(false);
        setMessages([{
          id: Date.now().toString(),
          text: 'Hello there!\n\nHow can I help you today?',
          sender: 'assistant',
          timestamp: Date.now()
        }]);
      } else {
        console.error('Failed to create new session');
      }
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  if (authError) {
    return (
      <div className="h-screen bg-white dark:bg-exc-primary flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            Session Expired
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Your session has expired or is not authenticated.
            Please reopen this app from the Helix platform.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white dark:bg-exc-primary relative">
      {/* Message Area with bottom padding to avoid input overlap */}
      <div className="h-full pb-20 relative">
        <MessageList 
          messages={messages} 
          isStreaming={isStreaming}
          messagesEndRef={messagesEndRef}
          messagesContainerRef={messagesContainerRef}
          onScroll={handleScroll}
          showSuggestedPrompts={isWelcomeScreen}
          onSuggestedPrompt={handleSuggestedPrompt}
        />
        
        {/* Scroll to Bottom Button */}
        {showScrollButton && (
          <button
            onClick={scrollToBottomClick}
            className="absolute bottom-24 right-4 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 
                       rounded-full p-3 shadow-lg hover:bg-gray-700 dark:hover:bg-gray-300 
                       transition-colors z-10"
            title="Scroll to bottom"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </button>
        )}
      </div>

      {/* Fixed Input Bar at Bottom - No Background */}
      <div className="absolute bottom-0 left-0 right-0">
        <MessageInput 
          onSendMessage={streamResponse}
          disabled={inputDisabled || isStreaming}
          onNewSession={handleNewSession}
        />
      </div>
    </div>
  );
};

export default ChatApp;